import { useState, useMemo } from "react";
import {
  BookOpen,
  Building2,
  Filter,
  Download,
  ChevronDown,
  ArrowUpRight,
  ArrowDownLeft,
  Minus,
  Plus,
  Loader2,
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { useLedgerInfinite, useLedgerSummary, useCreateManualLedgerEntry } from "../../../hooks/useLedger";
import { useBusinesses } from "../../../hooks/useBusinesses";
import { useAuth } from "../../../hooks/useAuth";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { formatCurrency } from "../../../utils/format";
import { ledgerApi } from "../../../lib/ledgerApi";
import type { LedgerEvent, LedgerEventType } from "../../../lib/ledgerApi";

// ── Helpers ────────────────────────────────────────────────────────────────────

const EVENT_TYPE_LABELS: Record<LedgerEventType, string> = {
  PAYROLL_FINALIZED:              "Payroll (Legacy)",
  PAYROLL_NET_DISBURSEMENT:       "Payroll Disbursement",
  PAYROLL_EMPLOYER_CONTRIBUTIONS: "Employer Contributions",
  LOAN_DISBURSED:                 "Loan Disbursed",
  LOAN_INSTALLMENT:               "Loan Repayment",
  CASH_ADVANCE_RELEASED:          "Cash Advance Disbursed",
  CASH_ADVANCE_REPAID:            "Cash Advance Repayment",
  WALLET_COMMISSION:              "Commission",
  WALLET_ENCASHMENT:              "Encashment",
  WALLET_ADVANCE_RELEASED:        "Wallet Advance Disbursed",
  WALLET_ADVANCE_REPAID:          "Wallet Advance Repayment",
  WALLET_CREDIT_ADDED:            "Wallet Credit",
  STORE_RECORD_CREATED:           "Store Revenue",
  STORE_RECORD_DELETED:           "Record Deleted",
  STORE_EXPENSE:                  "Store Expense",
  STORE_EXPENSE_DELETED:          "Expense Deleted",
  STORE_EXPENSE_UPDATED:          "Expense Updated",
  STORE_INCOME:                   "Store Income",
  STORE_INCOME_DELETED:           "Income Deleted",
  STORE_INCOME_UPDATED:           "Income Updated",
  SERVICE_REQUEST_APPROVED:       "Service Commission",
  SERVICE_REQUEST_VOIDED:         "Service Commission Voided",
  MANUAL_ENTRY:                   "Manual Entry",
  ADJUSTMENT_APPLIED:             "Adjustment",
};

// Direction: out = cash leaves company, in = cash returns/revenue, neutral = accrual/non-cash
// WALLET_COMMISSION is an accrual (company owes employee) — actual cash leaves on WALLET_ENCASHMENT
const EVENT_DIRECTION: Record<LedgerEventType, "out" | "in" | "neutral"> = {
  PAYROLL_FINALIZED:              "out",
  PAYROLL_NET_DISBURSEMENT:       "out",
  PAYROLL_EMPLOYER_CONTRIBUTIONS: "out",
  LOAN_DISBURSED:                 "out",
  LOAN_INSTALLMENT:               "in",
  CASH_ADVANCE_RELEASED:          "out",
  CASH_ADVANCE_REPAID:            "in",
  WALLET_COMMISSION:              "neutral",
  WALLET_ENCASHMENT:              "out",
  WALLET_ADVANCE_RELEASED:        "out",
  WALLET_ADVANCE_REPAID:          "in",
  WALLET_CREDIT_ADDED:            "neutral", // accrual — cash leaves on encashment
  STORE_RECORD_CREATED:           "in",
  STORE_RECORD_DELETED:           "out",    // reversal of store revenue inflow
  STORE_EXPENSE:                  "out",
  STORE_EXPENSE_DELETED:          "in",
  STORE_EXPENSE_UPDATED:          "neutral", // direction is dynamic, handled via getEventDir
  STORE_INCOME:                   "in",
  STORE_INCOME_DELETED:           "out",     // reversal of an inflow
  STORE_INCOME_UPDATED:           "neutral", // direction is dynamic, handled via getEventDir
  SERVICE_REQUEST_APPROVED:       "in",      // service income earned by the business; cash leaves on encashment
  SERVICE_REQUEST_VOIDED:         "out",     // reversal of service income accrual
  MANUAL_ENTRY:                   "neutral", // direction is dynamic, handled via getEventDir
  ADJUSTMENT_APPLIED:             "neutral",
};

const EVENT_TYPE_COLORS: Record<LedgerEventType, string> = {
  PAYROLL_FINALIZED:              "bg-[#dbeafe] text-[#1d4ed8]",
  PAYROLL_NET_DISBURSEMENT:       "bg-[#dbeafe] text-[#1d4ed8]",
  PAYROLL_EMPLOYER_CONTRIBUTIONS: "bg-[#e0f2fe] text-[#0369a1]",
  LOAN_DISBURSED:                 "bg-[#fef9c3] text-[#a16207]",
  LOAN_INSTALLMENT:               "bg-[#dcfce7] text-[#15803d]",
  CASH_ADVANCE_RELEASED:          "bg-[#fee2e2] text-[#b91c1c]",
  CASH_ADVANCE_REPAID:            "bg-[#dcfce7] text-[#15803d]",
  WALLET_COMMISSION:              "bg-[#f3e8ff] text-[#7e22ce]",
  WALLET_ENCASHMENT:              "bg-[#fce7f3] text-[#be185d]",
  WALLET_ADVANCE_RELEASED:        "bg-[#fff7ed] text-[#c2410c]",
  WALLET_ADVANCE_REPAID:          "bg-[#dcfce7] text-[#15803d]",
  WALLET_CREDIT_ADDED:            "bg-[#fef9c3] text-[#a16207]",
  STORE_RECORD_CREATED:           "bg-[#ccfbf1] text-[#0f766e]",
  STORE_RECORD_DELETED:           "bg-[#fee2e2] text-[#b91c1c]",
  STORE_EXPENSE:                  "bg-[#fee2e2] text-[#b91c1c]",
  STORE_EXPENSE_DELETED:          "bg-[#dcfce7] text-[#15803d]",
  STORE_EXPENSE_UPDATED:          "bg-[#fef9c3] text-[#a16207]",
  STORE_INCOME:                   "bg-[#dcfce7] text-[#15803d]",
  STORE_INCOME_DELETED:           "bg-[#fee2e2] text-[#b91c1c]",
  STORE_INCOME_UPDATED:           "bg-[#fef9c3] text-[#a16207]",
  SERVICE_REQUEST_APPROVED:       "bg-[#f3e8ff] text-[#7e22ce]",
  SERVICE_REQUEST_VOIDED:         "bg-[#fee2e2] text-[#b91c1c]",
  MANUAL_ENTRY:                   "bg-[#f1f5f9] text-[#475569]",
  ADJUSTMENT_APPLIED:             "bg-[#e0f2fe] text-[#0369a1]",
};

const ALL_TYPES: LedgerEventType[] = [
  "PAYROLL_NET_DISBURSEMENT",
  "PAYROLL_EMPLOYER_CONTRIBUTIONS",
  "PAYROLL_FINALIZED",
  "LOAN_DISBURSED",
  "LOAN_INSTALLMENT",
  "CASH_ADVANCE_RELEASED",
  "CASH_ADVANCE_REPAID",
  "WALLET_COMMISSION",
  "WALLET_ENCASHMENT",
  "WALLET_ADVANCE_RELEASED",
  "WALLET_ADVANCE_REPAID",
  "WALLET_CREDIT_ADDED",
  "STORE_RECORD_CREATED",
  "STORE_RECORD_DELETED",
  "STORE_EXPENSE",
  "STORE_EXPENSE_DELETED",
  "STORE_EXPENSE_UPDATED",
  "STORE_INCOME",
  "STORE_INCOME_DELETED",
  "STORE_INCOME_UPDATED",
  "SERVICE_REQUEST_APPROVED",
  "SERVICE_REQUEST_VOIDED",
  "MANUAL_ENTRY",
  "ADJUSTMENT_APPLIED",
];

const DATE_PRESETS = [
  { label: "This month",  getDates: () => ({ from: format(startOfMonth(new Date()), "yyyy-MM-dd"), to: format(new Date(), "yyyy-MM-dd") }) },
  { label: "Last 30 days", getDates: () => ({ from: format(subDays(new Date(), 30), "yyyy-MM-dd"), to: format(new Date(), "yyyy-MM-dd") }) },
  { label: "Last 90 days", getDates: () => ({ from: format(subDays(new Date(), 90), "yyyy-MM-dd"), to: format(new Date(), "yyyy-MM-dd") }) },
  { label: "Last month",  getDates: () => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return { from: format(startOfMonth(d), "yyyy-MM-dd"), to: format(endOfMonth(d), "yyyy-MM-dd") };
  }},
];

function formatShortDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function getEventDir(e: LedgerEvent): "out" | "in" | "neutral" {
  if (e.type === "ADJUSTMENT_APPLIED") return e.adjustmentDirection === "ADDITION" ? "out" : "in";
  if (e.type === "MANUAL_ENTRY") return e.manualDirection === "INCOME" ? "in" : "out";
  if (e.type === "STORE_EXPENSE_UPDATED") return e.manualDirection === "INCOME" ? "in" : "out";
  if (e.type === "STORE_INCOME_UPDATED") return e.manualDirection === "INCOME" ? "in" : "out";
  return EVENT_DIRECTION[e.type];
}

function DirectionIcon({ type, adjDir, manualDir }: {
  type: LedgerEventType;
  adjDir?: "DEDUCTION" | "ADDITION";
  manualDir?: "INCOME" | "EXPENSE";
}) {
  let dir: "out" | "in" | "neutral";
  if (type === "ADJUSTMENT_APPLIED") {
    dir = adjDir === "ADDITION" ? "out" : "in";
  } else if (type === "MANUAL_ENTRY" || type === "STORE_EXPENSE_UPDATED" || type === "STORE_INCOME_UPDATED") {
    dir = manualDir === "INCOME" ? "in" : "out";
  } else {
    dir = EVENT_DIRECTION[type];
  }

  if (dir === "out") return <ArrowUpRight className="h-3.5 w-3.5 text-[#dc2626]" />;
  if (dir === "in")  return <ArrowDownLeft className="h-3.5 w-3.5 text-[#16a34a]" />;
  return <Minus className="h-3.5 w-3.5 text-[#64748b]" />;
}

// ── CSV export ─────────────────────────────────────────────────────────────────

function exportCsv(items: LedgerEvent[]) {
  const rows = [
    ["Date", "Type", "Description", "Employee", "Amount", "Period", "Business ID", "Ref ID"],
    ...items.map((e) => [
      e.eventDate ? `${e.eventDate}T00:00:00.000Z` : e.createdAt,
      EVENT_TYPE_LABELS[e.type],
      `"${e.description.replace(/"/g, '""')}"`,
      e.employeeName ?? e.userId ?? "All Employees",
      e.amount.toFixed(2),
      e.period ?? "",
      e.businessId,
      e.refId,
    ]),
  ];
  const csv = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ledger-${format(new Date(), "yyyyMMdd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── NewManualEntryModal ────────────────────────────────────────────────────────

function NewManualEntryModal({
  businesses,
  onClose,
  onCreate,
  isPending,
}: {
  businesses: { businessId: string; name: string }[];
  onClose: () => void;
  onCreate: (payload: {
    businessId: string;
    eventDate: string;
    manualDirection: "INCOME" | "EXPENSE";
    amount: number;
    description: string;
  }) => void;
  isPending: boolean;
}) {
  const [businessId, setBusinessId] = useState("");
  const [eventDate, setEventDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [direction, setDirection] = useState<"INCOME" | "EXPENSE">("INCOME");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!businessId || !eventDate || isNaN(amt) || amt <= 0 || !description.trim()) return;
    onCreate({ businessId, eventDate, manualDirection: direction, amount: amt, description: description.trim() });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#e2e8f0] px-6 py-4">
          <h2 className="text-base font-semibold text-[#0f172a]">New Manual Entry</h2>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-[#475569]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="space-y-1">
            <label className="text-xs font-medium text-[#64748b]">Business</label>
            <select
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              required
              className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
            >
              <option value="">Select a business</option>
              {businesses.map((b) => (
                <option key={b.businessId} value={b.businessId}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748b]">Date</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                required
                className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748b]">Type</label>
              <div className="flex rounded-lg border border-[#e2e8f0] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDirection("INCOME")}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${direction === "INCOME" ? "bg-[#dcfce7] text-[#15803d]" : "text-[#64748b] hover:bg-[#f8fafc]"}`}
                >
                  Income
                </button>
                <button
                  type="button"
                  onClick={() => setDirection("EXPENSE")}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${direction === "EXPENSE" ? "bg-[#fee2e2] text-[#b91c1c]" : "text-[#64748b] hover:bg-[#f8fafc]"}`}
                >
                  Expense
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-[#64748b]">Amount (₱)</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              placeholder="0.00"
              className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-[#64748b]">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              maxLength={200}
              placeholder="e.g. Monthly rent payment"
              className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm text-[#475569] hover:bg-[#f8fafc] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1d3bb5] disabled:opacity-60 transition-colors"
            >
              {isPending ? "Saving…" : "Save Entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function LedgerPage() {
  const today = format(new Date(), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [businessId, setBusinessId] = useState("");
  const [selectedType, setSelectedType] = useState<LedgerEventType | "">("");
  const [showFilters, setShowFilters] = useState(false);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);

  const { isAdmin, activeBusinessId, businesses: authBusinesses, hasPageAccess, pagesForBusiness } = useAuth();
  const isSuperAdmin = isAdmin;
  // Anyone with ledger-page access can add manual entries (backend enforces per-business).
  const canAddEntry = hasPageAccess("admin:ledger");
  const createEntry = useCreateManualLedgerEntry();

  // Super-admin: fetch all businesses. Non-super-admin: use businesses from auth context.
  const { data: bizData } = useBusinesses(isAdmin);
  const businesses = isAdmin ? (bizData?.items ?? []) : authBusinesses;

  // Businesses the user may view/act on in the ledger — super-admin: all; others: only
  // those where their role grants admin:ledger (so they can't select a business that 403s).
  const accessibleBusinesses = isSuperAdmin
    ? businesses
    : businesses.filter((b) => {
        const p = pagesForBusiness(b.businessId);
        return p === "*" || p.includes("admin:ledger");
      });

  // For non-super-admin, default to their active business if it's ledger-accessible,
  // otherwise the first accessible one — so the first query always targets a business
  // they can actually view (avoids an immediate 403).
  const preferredBizId =
    accessibleBusinesses.some((b) => b.businessId === activeBusinessId)
      ? activeBusinessId ?? ""
      : accessibleBusinesses[0]?.businessId ?? "";
  const effectiveBizId = isSuperAdmin ? businessId : (businessId || preferredBizId);

  const filterParams = {
    from,
    to,
    ...(effectiveBizId ? { businessId: effectiveBizId } : {}),
    ...(selectedType ? { type: selectedType as LedgerEventType } : {}),
  };

  // Don't query until non-super-admin has a business ID resolved.
  const queryEnabled = isSuperAdmin || !!effectiveBizId;

  // Summary: separate query that covers ALL events for accurate totals.
  const { data: summaryData, isLoading: summaryLoading } = useLedgerSummary(filterParams, queryEnabled);

  // Paginated list — only used for the single-business flat table.
  const listEnabled = queryEnabled && !!effectiveBizId;
  const {
    data: pageData,
    isLoading: listLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useLedgerInfinite(filterParams, listEnabled);

  // Accumulate pages and sort by display date (eventDate ?? createdAt) descending.
  const items = useMemo(() => {
    const raw = pageData?.pages.flatMap((p) => p.items) ?? [];
    return [...raw].sort((a, b) => {
      const aKey = (a.eventDate ?? a.createdAt.slice(0, 10)) + "|" + a.createdAt;
      const bKey = (b.eventDate ?? b.createdAt.slice(0, 10)) + "|" + b.createdAt;
      return bKey.localeCompare(aKey);
    });
  }, [pageData]);

  const isLoading = listLoading;

  const bizNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of businesses) m.set(b.businessId, b.name);
    return m;
  }, [businesses]);

  const applyPreset = (preset: (typeof DATE_PRESETS)[0]) => {
    const { from: f, to: t } = preset.getDates();
    setFrom(f);
    setTo(t);
  };

  async function handleExportCsv() {
    setCsvExporting(true);
    try {
      const allItems: LedgerEvent[] = [];
      let lastKey: string | undefined;
      do {
        const resp = await ledgerApi.list({ ...filterParams, limit: 200, lastKey });
        allItems.push(...resp.items);
        lastKey = resp.nextKey;
      } while (lastKey);
      // Sort same as table: by display date desc
      allItems.sort((a, b) => {
        const aKey = (a.eventDate ?? a.createdAt.slice(0, 10)) + "|" + a.createdAt;
        const bKey = (b.eventDate ?? b.createdAt.slice(0, 10)) + "|" + b.createdAt;
        return bKey.localeCompare(aKey);
      });
      exportCsv(allItems);
    } finally {
      setCsvExporting(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#0f172a]">Transaction Ledger</h1>
            <p className="text-xs text-[#64748b]">All financial events across all businesses</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters((s) => !s)}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm text-[#475569] hover:bg-[#f8fafc] transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          {canAddEntry && (
            <button
              onClick={() => setShowNewEntry(true)}
              className="flex items-center gap-1.5 rounded-lg border border-[#2447d9] px-3 py-2 text-sm font-medium text-[#2447d9] hover:bg-[#eff3ff] transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Entry
            </button>
          )}
          {(summaryData?.eventCount ?? 0) > 0 && (
            <button
              onClick={handleExportCsv}
              disabled={csvExporting}
              className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-3 py-2 text-sm font-medium text-white hover:bg-[#1d3bb5] disabled:opacity-60 transition-colors"
            >
              {csvExporting ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Preparing…</>
              ) : (
                <><Download className="h-4 w-4" />Export CSV</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Business selector — always visible, primary organizing dimension */}
      <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3 py-2.5 shadow-sm">
        <Building2 className="h-4 w-4 shrink-0 text-[#2447d9]" />
        {isSuperAdmin ? (
          <>
            <select
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-sm font-medium text-[#0f172a] focus:outline-none cursor-pointer"
            >
              <option value="">All businesses</option>
              {businesses.map((b) => (
                <option key={b.businessId} value={b.businessId}>{b.name}</option>
              ))}
            </select>
            {businessId && (
              <button
                onClick={() => setBusinessId("")}
                className="shrink-0 text-xs text-[#94a3b8] hover:text-[#475569] transition-colors"
              >
                Clear
              </button>
            )}
          </>
        ) : (
          <select
            value={effectiveBizId}
            onChange={(e) => setBusinessId(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-sm font-medium text-[#0f172a] focus:outline-none cursor-pointer"
            disabled={accessibleBusinesses.length <= 1}
          >
            {accessibleBusinesses.length === 0 && <option value="">No ledger access</option>}
            {accessibleBusinesses.map((b) => (
              <option key={b.businessId} value={b.businessId}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Filter panel — date range + event type only */}
      {showFilters && (
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm space-y-4">
          {/* Date presets */}
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                className="rounded-md border border-[#e2e8f0] px-2.5 py-1 text-xs text-[#475569] hover:border-[#2447d9] hover:text-[#2447d9] transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* From */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748b]">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
              />
            </div>

            {/* To */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64748b]">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
              />
            </div>

            {/* Type */}
            <div className="space-y-1 col-span-2 md:col-span-1">
              <label className="text-xs font-medium text-[#64748b]">Event Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as LedgerEventType | "")}
                className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
              >
                <option value="">All types</option>
                {ALL_TYPES.map((t) => (
                  <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards — backend-computed totals covering all events for the filter */}
      {(summaryData || summaryLoading) && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-[#64748b] uppercase tracking-wide mb-1">Total Outflow</p>
            <p className="text-lg font-bold text-[#dc2626]">
              {summaryLoading ? "—" : `- ${formatCurrency(summaryData!.totalOutflow)}`}
            </p>
            <p className="text-xs text-[#94a3b8] mt-0.5">Payroll, disbursements</p>
          </div>
          <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-[#64748b] uppercase tracking-wide mb-1">Total Inflow</p>
            <p className="text-lg font-bold text-[#16a34a]">
              {summaryLoading ? "—" : `+ ${formatCurrency(summaryData!.totalInflow)}`}
            </p>
            <p className="text-xs text-[#94a3b8] mt-0.5">Repayments, store revenue</p>
          </div>
          <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-[#64748b] uppercase tracking-wide mb-1">Net</p>
            {summaryLoading ? (
              <p className="text-lg font-bold text-[#0f172a]">—</p>
            ) : (
              <p className={`text-lg font-bold ${summaryData!.net >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                {summaryData!.net >= 0 ? "+" : ""}{formatCurrency(summaryData!.net)}
              </p>
            )}
            <p className="text-xs text-[#94a3b8] mt-0.5">{summaryData?.eventCount ?? "—"} events</p>
          </div>
        </div>
      )}

      {/* Table — grouped by business (super-admin, no filter) or paginated flat table */}
      {!effectiveBizId ? (
        // All-businesses view: per-business summary cards from backend summary
        summaryLoading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : !summaryData?.byBusiness || Object.keys(summaryData.byBusiness).length === 0 ? (
          <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm py-16 text-center">
            <BookOpen className="h-10 w-10 text-[#cbd5e1] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#64748b]">No transactions found</p>
            <p className="text-xs text-[#94a3b8] mt-1">Try adjusting the date range or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(summaryData.byBusiness).map(([bizId, biz]) => {
              const bizName = bizNameMap.get(bizId) ?? bizId;
              return (
                <div key={bizId} className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm p-4">
                  <p className="text-sm font-semibold text-[#0f172a] mb-3 truncate" title={bizName}>{bizName}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#64748b]">Outflow</span>
                      <span className="text-sm font-semibold text-[#dc2626]">− {formatCurrency(biz.outflow)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#64748b]">Inflow</span>
                      <span className="text-sm font-semibold text-[#16a34a]">+ {formatCurrency(biz.inflow)}</span>
                    </div>
                    <div className="border-t border-[#f1f5f9] pt-2 flex justify-between items-center">
                      <span className="text-xs font-medium text-[#475569]">Net</span>
                      <span className={`text-sm font-bold ${biz.net >= 0 ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                        {biz.net >= 0 ? "+" : ""}{formatCurrency(biz.net)}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#94a3b8] pt-0.5">{biz.count} events</p>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        // Single-business paginated flat table
        <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : error ? (
            <div className="py-12 text-center text-[#64748b] text-sm">Failed to load ledger. Please try again.</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <BookOpen className="h-10 w-10 text-[#cbd5e1] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#64748b]">No transactions found</p>
              <p className="text-xs text-[#94a3b8] mt-1">Try adjusting the date range or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Employee</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Period</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {items.map((item) => {
                    const dir = getEventDir(item);
                    return (
                      <tr key={item.eventId} className="hover:bg-[#f8fafc] transition-colors">
                        <td className="px-4 py-3 text-[#475569] whitespace-nowrap text-xs">
                          {formatShortDate(item.eventDate ? `${item.eventDate}T00:00:00.000Z` : item.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${EVENT_TYPE_COLORS[item.type] ?? "bg-[#f1f5f9] text-[#475569]"}`}>
                            {EVENT_TYPE_LABELS[item.type] ?? item.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#0f172a] max-w-xs truncate" title={item.description}>
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-[#475569]">
                          <div className="text-xs">
                            {item.userId ? (
                              <>
                                <p className="font-medium text-[#0f172a]">{item.employeeName ?? "—"}</p>
                                <p className="text-[#94a3b8]">{item.userId}</p>
                              </>
                            ) : (
                              <p className="font-medium text-[#64748b] italic">All Employees</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#64748b] text-xs whitespace-nowrap">
                          {item.period ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <DirectionIcon type={item.type} adjDir={item.adjustmentDirection} manualDir={item.manualDirection} />
                            <span className={`font-semibold ${
                              dir === "out" ? "text-[#dc2626]" :
                              dir === "in"  ? "text-[#16a34a]" : "text-[#0f172a]"
                            }`}>
                              {formatCurrency(item.amount)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Load more / end-of-results footer */}
          {items.length > 0 && (
            <div className="border-t border-[#e2e8f0] px-4 py-3 flex items-center justify-center">
              {hasNextPage ? (
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm text-[#475569] hover:bg-[#f8fafc] disabled:opacity-60 transition-colors"
                >
                  {isFetchingNextPage ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />Loading…</>
                  ) : (
                    `Load more (${(summaryData?.eventCount ?? 0) - items.length} remaining)`
                  )}
                </button>
              ) : (
                <p className="text-xs text-[#94a3b8]">All {items.length} transactions loaded</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Manual Entry Modal */}
      {showNewEntry && (
        <NewManualEntryModal
          businesses={accessibleBusinesses}
          onClose={() => setShowNewEntry(false)}
          onCreate={(payload) => {
            createEntry.mutate(payload, {
              onSuccess: () => setShowNewEntry(false),
            });
          }}
          isPending={createEntry.isPending}
        />
      )}
    </div>
  );
}
